import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiFileText, FiTrash2, FiCopy, FiCheck, FiExternalLink } from 'react-icons/fi';
import { formatBytes, formatDate } from '../utils/helpers';
import { deleteFile } from '../lib/api';

const FileList = ({ files, onFilesChange }) => {
  const [copiedId, setCopiedId] = useState(null);

  const getFileLink = (file) => {
    // Return the direct URL to the physical file in /uploads/
    return `${window.location.origin}/uploads/${file.safeName}`;
  };

  const handleCopy = (file) => {
    const link = getFileLink(file);
    navigator.clipboard.writeText(link);
    setCopiedId(file.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Datei wirklich unwiderruflich löschen?')) {
      try {
        await deleteFile(id);
        onFilesChange();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  if (files.length === 0) {
    return (
      <div className="p-16 text-center">
        <div className="mx-auto w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
          <SafeIcon icon={FiFileText} className="w-10 h-10 text-gray-300" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Noch keine Dateien vorhanden</h3>
        <p className="text-gray-500 max-w-sm mx-auto">
          Laden Sie Ihre erste Datei hoch, um sie hier zu verwalten.
        </p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-gray-100">
      {files.map((file) => (
        <li key={file.id} className="p-6 sm:px-8 hover:bg-gray-50 transition-colors group">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center flex-1 min-w-0">
              <div className="p-3 bg-white border border-gray-200 rounded-xl mr-5 flex-shrink-0 shadow-sm group-hover:border-[#005b9f]/30 transition-colors">
                <SafeIcon icon={FiFileText} className="w-7 h-7 text-[#005b9f]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-bold text-gray-900 truncate mb-1">
                  {file.originalName}
                </p>
                <div className="flex items-center text-sm text-gray-500 gap-3">
                  <span>{formatDate(file.uploadDate)}</span>
                  <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                  <span className="font-medium">{formatBytes(file.size)}</span>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="bg-white border border-gray-200 p-2 rounded-lg flex items-center gap-2 w-full sm:w-auto shadow-sm">
                <span className="text-xs text-gray-500 truncate w-full sm:w-64 font-mono select-all px-2">
                  {getFileLink(file)}
                </span>
                <button 
                  onClick={() => handleCopy(file)}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${copiedId === file.id ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-white border border-gray-200 text-[#005b9f] hover:bg-[#f0f7fb] hover:border-[#005b9f]/30'}`}
                >
                  {copiedId === file.id ? (
                    <><SafeIcon icon={FiCheck} /> Kopiert</>
                  ) : (
                    <><SafeIcon icon={FiCopy} /> Link kopieren</>
                  )}
                </button>
              </div>

              <div className="flex items-center gap-2 ml-auto sm:ml-0">
                <a 
                  href={getFileLink(file)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#f0f7fb] text-[#005b9f] rounded-lg text-xs font-bold flex items-center gap-2 hover:bg-[#005b9f] hover:text-white transition-all"
                >
                  <SafeIcon icon={FiExternalLink} /> Datei öffnen
                </a>
                <button 
                  onClick={() => handleDelete(file.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                >
                  <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default FileList;